import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAbfCopilotContext } from '../copilot/AbfCopilotLayout';
import styled from 'styled-components';
import {useLocation} from 'react-router-dom';
import { AlertTriangle, Check, CheckCircle2, CircleDot, RotateCcw, XCircle } from 'lucide-react';
import {
  apiErrorMessage,
  cancelFundingRequest,
  confirmFundingRequest,
  createControlledDrawRequest,
  createRepaymentRequest,
  decideControlledDrawRequest,
  getFundingWorkspace,
  previewFundingGates,
  proposeFundingReversal,
  releaseControlledDrawRequest,
  retryFundingPosting,
  getDisbursementAccount, type DisbursementAccountRow,
} from '../api/abfApi';
import type {
  DrawRequestRecord,
  FundingGate,
  FundingGateEvaluation,
  FundingWorkspaceReadModel,
  RepaymentKind,
} from '../types/funding';
import { type ActionFeedbackState } from '../components/ActionFeedback';
import { StatusChip, type StatusTone } from '../components/StatusChip';
import { Button, Card, CardHead, FieldLabel, INK, Input, fieldBase } from '../onboarding/review/reviewStyles';
import { Layout, Note, Readable, Signoff, SmallButton, TaskButton, TaskRail, Work } from '../onboarding/review/contractReviewStyles';
import { formatMoney, isValidAmountShorthand, parseAmountShorthand, replaceShorthandOnBlur } from '../../../utils/amountUtils';
import { fmtDate, fmtMoney, plural } from '../presentation/format';

import ClosingAdvancePanel from './ClosingAdvancePanel';
import { WorkspaceDisclosure, WorkspaceGuide } from '../components/WorkspaceExperience';
import HoldersPanel from './HoldersPanel';
import DisbursementAccountPanel from './DisbursementAccountPanel';
import { DateField } from '../../../components/shared/DateField';

// ---------------------------------------------------------------------------
// Review standard (approved 2026-09-11): same shell, journey, strip, rail and bar as Period review.
// ---------------------------------------------------------------------------

const Shell = styled.div`
  color: ${INK.body};
  font-size: 14px;
  line-height: 1.55;
  margin-top: 14px;
  h2, h3, p { margin: 0; }
  h2 { font-size: 17px; }
  h3 { font-size: 15px; }
  button, input, select, textarea { font-family: inherit; }
`;

const Journey = styled.ol`
  display: grid; grid-template-columns: repeat(3, 1fr); list-style: none;
  padding: 0; margin: 0 0 18px; border: 1px solid ${INK.line}; border-radius: 10px; overflow: hidden;
  li { display: flex; gap: 12px; align-items: center; padding: 13px 16px; background: #0c1626; }
  li + li { border-left: 1px solid ${INK.line}; }
  li[data-current='true'] { background: #12273c; box-shadow: inset 0 -2px ${INK.accent}; }
  .step-icon { display: grid; place-items: center; width: 28px; height: 28px; border: 1px solid ${INK.lineStrong}; border-radius: 50%; color: #b5c9dd; flex-shrink: 0; }
  .done { color: #7cddbe; border-color: #376e60; }
  strong { display: block; font-size: 14px; }
  small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 2px; }
  @media (max-width: 700px) { grid-template-columns: 1fr; li + li { border-left: 0; border-top: 1px solid ${INK.line}; } }
`;

const Strip = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; margin-bottom: 8px;
  > div { padding: 12px 14px; border: 1px solid ${INK.line}; border-radius: 10px; background: ${INK.panel}; }
  > div[data-key='available'] { border-color: #376e60; background: #0d1f22; }
  span { display: block; color: ${INK.faint}; font-size: 11px; font-weight: 750; letter-spacing: .08em; text-transform: uppercase; }
  strong { display: block; margin-top: 4px; font-size: 18px; letter-spacing: -.01em; color: ${INK.strong}; font-variant-numeric: tabular-nums; }
  strong[data-tone='green'] { color: #7cddbe; }
  strong[data-tone='amber'] { color: #edcc82; }
  strong[data-tone='red'] { color: #fca5a5; }
  em { display: inline-block; margin-top: 5px; padding: 1px 7px; border: 1px solid ${INK.lineStrong}; border-radius: 999px; color: ${INK.soft}; font-size: 10px; font-style: normal; font-weight: 750; letter-spacing: .06em; text-transform: uppercase; }
  small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 4px; }
`;

const StripNote = styled.p`
  margin: 0 0 16px !important; color: ${INK.muted}; font-size: 12px;
`;

const StageLine = styled.ol`
  display: flex; gap: 4px; list-style: none; padding: 0; margin: 6px 0 0;
  li { width: 14px; height: 6px; border-radius: 3px; background: #26364a; }
  li[data-state='DONE'] { background: #4fd1a5; }
  li[data-state='OPEN'] { background: ${INK.accent}; }
  li[data-state='BLOCKED'] { background: #f0a24a; }
  li[data-state='TERMINAL'] { background: #6b7a8c; }
`;

const Facts = styled.dl`
  display: grid; grid-template-columns: max-content 1fr; gap: 6px 14px; margin: 0;
  dt { color: ${INK.muted}; font-size: 13px; }
  dd { margin: 0; color: ${INK.body}; font-size: 13px; overflow-wrap: anywhere; }
`;

const Grid2 = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; align-items: start; margin-top: 14px;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;

const FormGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr)); gap: 10px 12px; margin-top: 12px;
`;

const Select = styled.select`
  ${fieldBase}
  option { background: #0f172a; color: ${INK.body}; }
`;

const ParsedAmount = styled.span`
  display: block; margin-top: 4px; color: #7dd3fc; font-size: 12px; font-weight: 600;
  &[data-bad='true'] { color: #fca5a5; }
`;

const Gates = styled.ul`
  list-style: none; padding: 0; margin: 10px 0 0; display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 6px 14px;
  li { display: flex; gap: 8px; align-items: flex-start; font-size: 13px; color: ${INK.soft}; }
  li[data-outcome='FAIL'] { color: #fca5a5; }
  li[data-outcome='WARN'] { color: #f0c77a; }
  li svg { flex-shrink: 0; margin-top: 3px; }
  li small { display: block; color: ${INK.faint}; }
`;

const Bar = styled.div`
  position: sticky; bottom: 0; z-index: 5; display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
  gap: 12px; margin-top: 18px; padding: 12px 14px; border: 1px solid ${INK.line}; border-radius: 10px;
  background: rgba(9,18,31,.96); backdrop-filter: blur(4px);
  .state { color: ${INK.muted}; font-size: 13px; } .state strong { color: ${INK.strong}; }
  .actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  input { ${fieldBase} width: 260px; }
`;

const Attention = styled.article`
  padding: 12px 14px; border: 1px solid rgba(240,162,74,.5); border-radius: 10px; background: ${INK.panel}; margin-bottom: 14px;
  display: flex; gap: 10px; align-items: flex-start;
  strong { color: ${INK.strong}; }
  p { color: ${INK.muted}; font-size: 13px; margin-top: 3px; }
  svg { color: #f0a24a; flex-shrink: 0; margin-top: 2px; }
`;

const Timeline = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px;
  th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid ${INK.line}; vertical-align: top; }
  th { color: ${INK.faint}; font-size: 11px; font-weight: 750; letter-spacing: .06em; text-transform: uppercase; }
  td.when { white-space: nowrap; color: ${INK.muted}; }
`;

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

const money = (v: unknown, currency?: string | null) => v == null || v === '' ? '—' : formatMoney(Number(v), currency || 'USD');
const date = (v: unknown) => v ? fmtDate(String(v)) : '—';
/**
 * The server's card subtext names the table it read from, in parentheses, and
 * writes dates as ISO. On screen the plain-English part stays, dates read like
 * the header's, and the technical name moves to the tooltip.
 */
const TECH_REF = /\s*\(((?:[a-z]+\.)?[a-z]+_[a-z_.]+)[^)]*\)/g;
const readable = (text: string | null | undefined) => (text || '')
  .replace(TECH_REF, '')
  .replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (_, iso: string) => fmtDate(iso))
  .trim();
const technicalRef = (text: string | null | undefined) => {
  const found = (text || '').match(TECH_REF);
  return found ? found.map(f => f.trim().replace(/^\(|\)$/g, '')).join('; ') : undefined;
};
const stamp = (v: unknown) => v ? new Date(String(v).replace(' ', 'T')).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const who = (v: unknown) => v ? String(v) : '—';
const plain = (v: unknown, fallback = '—') => v == null || v === '' ? fallback : String(v).replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());

type Stage = 'requested' | 'gates' | 'approved' | 'released' | 'confirmed' | 'posted';
type StageState = 'DONE' | 'OPEN' | 'BLOCKED' | 'TERMINAL' | 'TODO';
const STAGES: Stage[] = ['requested', 'gates', 'approved', 'released', 'confirmed', 'posted'];
const stageLabel: Record<Stage, string> = { requested: 'Requested', gates: 'Gates', approved: 'Approved', released: 'Released', confirmed: 'Confirmed', posted: 'Posted' };

const stageStates = (r: DrawRequestRecord): Record<Stage, StageState> => {
  const s = String(r.status);
  const repayment = r.request_type === 'REPAYMENT';
  const reversal = r.request_type === 'REVERSAL';
  const terminal = ['REJECTED', 'CANCELLED', 'EXPIRED'].includes(s);
  const held = Boolean(r.attention_reason);
  const order = ['PROPOSED', 'PENDING_APPROVAL', 'APPROVED', 'RESERVED', 'RELEASED', 'POSTED', 'REVERSED'];
  const idx = order.indexOf(s);
  const done = (n: number) => idx >= n;
  return {
    requested: 'DONE',
    gates: held ? 'BLOCKED' : done(2) || s === 'FAILED' ? 'DONE' : 'OPEN',
    approved: done(2) || s === 'FAILED' ? 'DONE' : terminal ? 'TERMINAL' : held ? 'TODO' : 'OPEN',
    released: repayment || reversal ? 'DONE' : done(4) || s === 'FAILED' ? 'DONE' : terminal ? 'TERMINAL' : done(2) && !held ? 'OPEN' : 'TODO',
    confirmed: done(5) ? 'DONE' : s === 'FAILED' ? 'BLOCKED' : terminal ? 'TERMINAL' : (repayment ? done(2) : done(4)) && !held ? 'OPEN' : 'TODO',
    posted: done(5) ? 'DONE' : s === 'FAILED' ? 'BLOCKED' : terminal ? 'TERMINAL' : 'TODO',
  };
};

const statusTone = (status: string, attention?: string | null): StatusTone =>
  attention ? 'warning'
    : ['POSTED', 'REVERSED'].includes(status) ? 'done'
    : ['REJECTED', 'CANCELLED', 'EXPIRED', 'FAILED'].includes(status) ? 'critical'
    : 'progress';
const statusWord = (r: DrawRequestRecord) => r.attention_reason ? 'Needs attention'
  : r.status === 'PENDING_APPROVAL' ? 'Awaiting checker'
  : r.status === 'RESERVED' ? 'Approved · reserved'
  : r.status === 'APPROVED' ? 'Approved'
  : r.status === 'RELEASED' ? 'Released · awaiting bank confirmation'
  : r.status === 'FAILED' ? 'Posting failed'
  : plain(r.status);
const typeWord = (r: DrawRequestRecord) => r.request_type === 'REPAYMENT' ? plain(r.repayment_kind || 'Repayment')
  : r.request_type === 'REVERSAL' ? 'Reversal' : r.request_type === 'CLOSING_ADVANCE' ? 'Closing advance' : 'Draw';

const GateList: React.FC<{ gates: FundingGate[] }> = ({ gates }) => (
  <Gates>
    {gates.map(g => (
      <li key={g.key} data-outcome={g.outcome}>
        {g.outcome === 'FAIL' ? <XCircle size={14} /> : g.outcome === 'WARN' ? <AlertTriangle size={14} /> : <Check size={14} color="#4fd1a5" />}
        <span>{g.label}<small>{g.detail}</small></span>
      </li>
    ))}
  </Gates>
);

interface Props {
  facilityId: number;
  currency?: string | null;
  userRole?: string | null;
  onFeedback: (feedback: ActionFeedbackState | null) => void;
}

/** F-MK-041: the account the borrower's notice names, offered for registration so nobody retypes it. */
function noticeSuggestion(requests: DrawRequestRecord[], selectedInstructions: Record<string, unknown> | undefined, facility: Record<string, unknown> | undefined) {
  const fromRequest = (r: DrawRequestRecord) => (r as unknown as { settlement_instructions_json?: unknown }).settlement_instructions_json;
  const parsed = (v: unknown): Record<string, unknown> | null => {
    if (!v) return null;
    if (typeof v === 'string') { try { return JSON.parse(v); } catch { return null; } }
    return typeof v === 'object' ? v as Record<string, unknown> : null;
  };
  const candidates = [selectedInstructions, ...requests.map(r => parsed(fromRequest(r)))];
  const found = candidates.find(c => c && c.bankName && c.accountLast4);
  if (!found) return null;
  return { bankName: String(found.bankName), accountLast4: String(found.accountLast4), routingNumber: found.routing ? String(found.routing) : '',
    beneficiaryName: found.beneficiary ? String(found.beneficiary) : String(facility?.obligor_name || '') };
}

const MAKER_ROLES = new Set(['PM', 'PORTFOLIO_MANAGER', 'TRADER', 'OPERATIONS', 'OPERATIONS_ANALYST', 'MIDDLE_OFFICE_ADMIN', 'ADMIN', 'PLATFORM_ADMIN']);
const CHECKER_ROLES = new Set(['RISK', 'RISK_MANAGER', 'COMPLIANCE', 'COMPLIANCE_OFFICER', 'OPERATIONS', 'OPERATIONS_ANALYST', 'MIDDLE_OFFICE_ADMIN', 'ADMIN', 'PLATFORM_ADMIN']);

type Runner = (key: string, action: () => Promise<unknown>, success: string) => Promise<boolean>;

// ---------------------------------------------------------------------------
// Workspace
// ---------------------------------------------------------------------------

const FundingWorkspace: React.FC<Props> = ({ facilityId, currency = 'USD', userRole, onFeedback }) => {
  const location = useLocation();
  const linkedRequest = new URLSearchParams(location.search).get('request');
  const linkedRequestId = linkedRequest && /^[1-9][0-9]*$/.test(linkedRequest) && Number.isSafeInteger(Number(linkedRequest)) ? Number(linkedRequest) : undefined;
  const [model, setModel] = useState<FundingWorkspaceReadModel | null>(null);
  const [selectedId, setSelectedId] = useState<number | 'new' | undefined>();
  useAbfCopilotContext({selectedRecord: typeof selectedId === 'number' ? {type: 'DRAW_REQUEST', id: selectedId} : undefined});
  const [busy, setBusy] = useState<string | null>(null);
  const workRef = useRef<HTMLElement>(null);
  const ccy = currency || 'USD';

  const load = useCallback(async (requestId?: number, keepNew = false) => {
    try {
      const result = await getFundingWorkspace(facilityId, requestId);
      setModel(result);
      if (!keepNew) {
        const selected = result.selectedRequest as DrawRequestRecord;
        setSelectedId(selected?.id ?? 'new');
      }
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'Unable to load the funding workspace.') });
    }
  }, [facilityId, onFeedback]);

  useEffect(() => { void load(linkedRequestId); }, [load, linkedRequestId]);

  const run: Runner = async (key, action, success) => {
    setBusy(key);
    try {
      const result = await action() as DrawRequestRecord | undefined;
      onFeedback({ tone: 'success', message: success });
      await load(result?.id ?? (typeof selectedId === 'number' ? selectedId : undefined));
      return true;
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The action was refused.') });
      await load(typeof selectedId === 'number' ? selectedId : undefined, selectedId === 'new');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const requests = useMemo(() => model?.requests || [], [model?.requests]);
  const selected = typeof selectedId === 'number' ? (model?.selectedRequest as DrawRequestRecord | undefined) : undefined;
  const role = String(userRole || '').toUpperCase();
  const closingAdvance = requests.find(r => r.request_type === 'CLOSING_ADVANCE'
    && ['PROPOSED', 'PENDING_APPROVAL', 'RESERVED', 'RELEASED'].includes(String(r.status)));

  if (!model) return <Shell><Note>Loading funding…</Note></Shell>;

  const journeyStep = selectedId === 'new' || !selected ? 1
    : ['PENDING_APPROVAL', 'RESERVED'].includes(String(selected.status)) ? 2 : 3;
  const attentionCount = model.queueSummary.needsAttention;

  const select = (id: number | 'new') => {
    setSelectedId(id);
    // F-S3-006: the rail refreshes on every selection, including "New", so a post made in
    // another session is never shown as still reserved.
    if (id !== 'new') void load(id); else void load(undefined, true);
    if (window.matchMedia('(max-width: 1000px)').matches) workRef.current?.scrollIntoView({ block: 'start' });
  };

  return <Shell>
    <Journey aria-label="How funding works">
      <li data-current={journeyStep === 1}><span className={`step-icon ${journeyStep > 1 ? 'done' : ''}`}>1</span><div><strong>Request the money</strong><small>Borrower's notice or entry · the gates run before you submit</small></div></li>
      <li data-current={journeyStep === 2}><span className={`step-icon ${journeyStep > 2 ? 'done' : ''}`}>2</span><div><strong>Checker approves, operations releases</strong><small>Someone else approves and reserves · the wire goes out with its details recorded</small></div></li>
      <li data-current={journeyStep === 3}><span className="step-icon">3</span><div><strong>Bank confirms, IBOR posts</strong><small>The confirmation is the evidence · one lifecycle event, one trade, one position</small></div></li>
    </Journey>

    <Strip aria-label="Availability walk">
      {model.availability.steps.map(s => {
        // Drawn and reserved are deductions in the walk. The card shows the
        // amount itself, never a negative drawn balance, and says it is deducted.
        const deduction = s.key === 'drawn' || s.key === 'reserved';
        const shown = s.amount == null ? null : deduction ? Math.abs(Number(s.amount)) : Number(s.amount);
        return (
          <div key={s.key} data-key={s.key}>
            <span>{deduction ? `Less: ${s.label}` : s.label}</span>
            <strong data-tone={s.key === 'available' ? (Number(s.amount) > 0 ? 'green' : 'red') : deduction ? 'amber' : undefined}>
              {shown == null ? (s.key === 'available' ? 'Not available' : '—') : fmtMoney(shown, ccy)}
            </strong>
            <em>{plain(s.authority)}</em>
            <small title={technicalRef(s.detail)}>{readable(s.detail)}</small>
          </div>
        );
      })}
    </Strip>
    <StripNote>
      Pending, not yet approved: {fmtMoney(model.availability.pending, ccy)} · {model.availability.unsupportedReason ? `no figure: ${model.availability.unsupportedReason.split(':')[0].toLowerCase()}${model.availability.holds > 0 ? `; also ${plural(model.availability.holds, 'active hold')}` : ''}` : model.availability.holds > 0 ? `${plural(model.availability.holds, 'active hold')} ${model.availability.holds === 1 ? 'makes' : 'make'} availability zero` : 'no active holds'} · {model.availability.derivation}.
    </StripNote>

    {/* Work plan item F: outside the revolving period the gate refuses new money; say so where requests are made. */}
    {model.availability.phase?.drawRefusal && <Note style={{ marginBottom: 14 }}>
      <strong>No new draws: {model.availability.phase.drawRefusal}.</strong>
      <p>The facility is {String(model.availability.phase.code).toLowerCase().replace(/_/g, ' ')}{model.availability.phase.until ? ` until ${date(model.availability.phase.until)}` : ''}. Repayments and reversals still run; a draw request fails the term-mechanics gate.</p>
    </Note>}

    {closingAdvance && (
      <ClosingAdvancePanel facilityId={facilityId} request={closingAdvance} currency={ccy}
        canPrepare={MAKER_ROLES.has(role)} canDecide={CHECKER_ROLES.has(role)}
        onChanged={async message => { onFeedback({ tone: 'success', message }); await load(typeof selectedId === 'number' ? selectedId : undefined); }} />
    )}

    <WorkspaceDisclosure><summary>Holders & wire instructions <small>Review or update the governed funding setup</small></summary>
    {/* WS3: who holds the facility and how every booked amount splits. */}
    <HoldersPanel facilityId={facilityId} currency={ccy} canMake={MAKER_ROLES.has(role)} canCheck={CHECKER_ROLES.has(role)} onFeedback={onFeedback} />
    {/* Fix list 2: the account every draw is wired to, registered once. */}
    <DisbursementAccountPanel facilityId={facilityId} canMake={MAKER_ROLES.has(role)} canCheck={CHECKER_ROLES.has(role)} onFeedback={onFeedback}
      suggestion={noticeSuggestion(requests, model.settlement?.instructions as Record<string, unknown> | undefined, model.facility)} />

    </WorkspaceDisclosure>
    {attentionCount > 0 && <Note style={{ marginBottom: 14 }}>
      <strong>{attentionCount} request{attentionCount === 1 ? '' : 's'} need{attentionCount === 1 ? 's' : ''} attention.</strong>
      <p>A gate that passed earlier now fails. The request is held, not cancelled; the next action re-runs the gates and proceeds when they pass.</p>
    </Note>}

    <Layout>
      <TaskRail aria-label="Funding requests">
        <div className="rail-heading"><h2>Requests</h2><p>Draws, repayments and reversals, by notice or by entry</p></div>
        <div className="tasks">
          <TaskButton type="button" $active={selectedId === 'new'} aria-pressed={selectedId === 'new'} onClick={() => select('new')}>
            <div className="category">New</div>
            <strong>Request a draw or a repayment</strong>
            <span className="reason" style={{ color: '#a6d8f5' }}><CircleDot size={12} />Type an amount to see the gates</span>
          </TaskButton>
          {requests.map(r => {
            const stages = stageStates(r);
            const finished = ['POSTED', 'REVERSED'].includes(String(r.status));
            const closed = ['REJECTED', 'CANCELLED', 'EXPIRED', 'FAILED'].includes(String(r.status));
            return <TaskButton key={r.id} type="button" $active={selectedId === r.id} aria-pressed={selectedId === r.id} onClick={() => select(r.id)}>
              <div className="category">#{r.id} · {typeWord(r)} · {r.origin === 'DOCUMENT' ? 'from notice' : r.origin === 'CONTRACT_ACTIVATION' ? 'from agreement' : 'entered'}</div>
              <strong>{money(r.amount, ccy)} · {date(r.value_date || r.requested_date)}</strong>
              <StageLine aria-label="Progress">{STAGES.map(s => <li key={s} data-state={stages[s]} title={stageLabel[s]} />)}</StageLine>
              <span className="reason" style={{ color: r.attention_reason ? '#f0a24a' : finished ? '#7cddbe' : closed ? '#a8bacd' : undefined }}>
                {finished ? <Check size={12} /> : <CircleDot size={12} />}{statusWord(r)}
              </span>
            </TaskButton>;
          })}
          {!requests.length && <p className="empty">No requests yet.</p>}
        </div>
      </TaskRail>

      <Work ref={workRef} aria-label="Selected request">
        {selectedId === 'new' || !selected
          ? <NewRequestPane key={`new-${facilityId}`} facilityId={facilityId} currency={ccy} model={model} busy={busy} run={run} />
          : <RequestPane key={`${facilityId}-${selected.id}`} facilityId={facilityId} currency={ccy} model={model} request={selected} busy={busy} run={run} />}
      </Work>
    </Layout>
  </Shell>;
};

// ---------------------------------------------------------------------------
// New request
// ---------------------------------------------------------------------------

interface PaneBase { facilityId: number; currency: string; model: FundingWorkspaceReadModel; busy: string | null; run: Runner; }

const NewRequestPane: React.FC<PaneBase> = ({ facilityId, currency, model, busy, run }) => {
  const [kind, setKind] = useState<'DRAW' | 'REPAYMENT'>('DRAW');
  const [amount, setAmount] = useState('');
  const [valueDate, setValueDate] = useState(model.businessDate || '');
  const [repaymentKind, setRepaymentKind] = useState<RepaymentKind>('SCHEDULED');
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState<FundingGateEvaluation | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const parsed = parseAmountShorthand(amount);
  const valid = isValidAmountShorthand(amount);
  const allowed = kind === 'DRAW' ? model.actions.create : model.actions.createRepayment;

  useEffect(() => {
    if (!valid) { setPreview(null); return; }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setPreviewing(true);
      try {
        const result = await previewFundingGates(facilityId, {
          requestType: kind === 'DRAW' ? 'STANDARD' : 'REPAYMENT',
          repaymentKind: kind === 'REPAYMENT' ? repaymentKind : undefined,
          amount: parsed, valueDate: valueDate || undefined,
        });
        if (!cancelled) setPreview(result);
      } catch (error) {
        if (!cancelled) setPreview({ passed: false, blockingGate: 'preview', blockingDetail: apiErrorMessage(error, 'Gate preview failed'), gates: [] });
      } finally { if (!cancelled) setPreviewing(false); }
    }, 350);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [facilityId, kind, repaymentKind, valid, parsed, valueDate]);

  const blocked = preview !== null && !preview.passed;
  const reason = !allowed.allowed ? allowed.disabledReason
    : !valid ? 'Enter a positive amount — 10MM, 500M or 10,000,000.'
    : blocked ? `Blocked: ${preview?.blockingDetail || preview?.blockingGate}` : null;

  const submit = () => run('create',
    () => kind === 'DRAW'
      ? createControlledDrawRequest(facilityId, { amount: parsed, requestedDate: valueDate, notes })
      : createRepaymentRequest(facilityId, { amount: parsed, valueDate, repaymentKind, notes }),
    kind === 'DRAW'
      ? `Draw request for ${money(parsed, currency)} created; nothing is reserved or posted until a checker approves it.`
      : `${plain(repaymentKind)} request for ${money(parsed, currency)} created; it posts after approval and bank confirmation.`);

  return <>
    <div className="work-heading"><div><h2>New request</h2><p>By entry here, or by uploading the borrower's notice in Middle Office → Document Processing. Both create the same request and run the same gates.</p></div></div>
    <Card>
      <CardHead><div><h2>{kind === 'DRAW' ? 'Draw' : 'Repayment, prepayment or payoff'}</h2><p>Amounts take shorthand. The value date defaults to the business date, {date(model.businessDate)}.</p></div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Button type="button" $variant={kind === 'DRAW' ? 'primary' : 'ghost'} onClick={() => setKind('DRAW')}>Draw</Button>
          <Button type="button" $variant={kind === 'REPAYMENT' ? 'primary' : 'ghost'} onClick={() => setKind('REPAYMENT')}>Repayment</Button>
        </div>
      </CardHead>
      <FormGrid>
        <FieldLabel>Amount<Input inputMode="decimal" placeholder="10MM · 500M · 10,000,000" value={amount} onChange={e => setAmount(e.target.value)} onBlur={e => setAmount(replaceShorthandOnBlur(e.target.value))} />
          {amount.trim() && !valid && <ParsedAmount data-bad>Not a valid amount</ParsedAmount>}</FieldLabel>
        <FieldLabel>Value date<DateField value={valueDate} today={model.businessDate || undefined} onChange={setValueDate} /></FieldLabel>
        {kind === 'REPAYMENT' && <FieldLabel>Kind<Select value={repaymentKind} onChange={e => setRepaymentKind(e.target.value as RepaymentKind)}>
          <option value="SCHEDULED">Scheduled repayment</option><option value="PREPAYMENT">Prepayment</option><option value="PAYOFF">Payoff (full drawn balance)</option></Select></FieldLabel>}
        <FieldLabel style={{ gridColumn: '1 / -1' }}>Note<Input value={notes} placeholder="Purpose, notice reference, evidence" onChange={e => setNotes(e.target.value)} /></FieldLabel>
      </FormGrid>
    </Card>
    <Card style={{ marginTop: 14 }}>
      <CardHead><div><h2>Gates</h2><p>{previewing ? 'Evaluating…' : preview ? (preview.passed ? 'Every gate passes for this request.' : `Blocked by ${preview.blockingGate}.`) : 'Type an amount and the checklist runs live, before anything is submitted.'}</p></div>
        {preview && <StatusChip tone={preview.passed ? 'done' : 'critical'} label={preview.passed ? 'Ready to submit' : 'Blocked'} size="md" />}</CardHead>
      {preview?.gates?.length ? <GateList gates={preview.gates} /> : <p style={{ color: INK.muted, fontSize: 13 }}>No checklist yet.</p>}
    </Card>
    <Bar>
      <div className="state">{reason ? <>Cannot submit: <strong>{reason}</strong></> : <>Ready: <strong>{money(parsed, currency)}</strong> on {date(valueDate)} · the same gates run again at approval, release and confirmation</>}</div>
      <div className="actions"><Button type="button" $variant="primary" disabled={Boolean(reason) || busy !== null} onClick={() => void submit()}>{busy === 'create' ? 'Submitting…' : kind === 'DRAW' ? 'Submit draw request' : 'Submit repayment request'}</Button></div>
    </Bar>
  </>;
};

// ---------------------------------------------------------------------------
// Selected request
// ---------------------------------------------------------------------------

const RequestPane: React.FC<PaneBase & { request: DrawRequestRecord }> = ({ facilityId, currency, model, request, busy, run }) => {
  const a = model.actions;
  const isRepayment = request.request_type === 'REPAYMENT';
  const isReversal = request.request_type === 'REVERSAL';
  const primary = a.confirm.allowed ? 'confirm' : a.release.allowed ? 'release' : a.approve.allowed ? 'approve' : a.retryPosting.allowed ? 'retry' : null;
  const latest = model.latestGates as FundingGateEvaluation | undefined;
  const lineage = model.officialLineage || {};
  const instructions = (model.settlement.instructions || {}) as Record<string, unknown>;
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [release, setRelease] = useState({ valueDate: request.value_date ? String(request.value_date).slice(0, 10) : (model.businessDate || ''), wireReference: '', bankName: '', accountLast4: '', routing: '', beneficiary: '' });
  const [confirm, setConfirm] = useState({ confirmationReference: '', confirmationDocumentId: '', confirmedValueDate: '', confirmedAmount: '' });
  const [reversal, setReversal] = useState({ reasonCode: 'BOOKING_ERROR' as 'BOOKING_ERROR' | 'WIRE_RETURNED' | 'DUPLICATE' | 'OTHER', reason: '', evidenceReference: '', reversalDate: model.businessDate || '' });
  const [timelineOpen, setTimelineOpen] = useState(true);
  const [lineageOpen, setLineageOpen] = useState(false);

  // Fix list 2: with a registered disbursement account the wire goes there; only the reference is typed.
  const [registered, setRegistered] = useState<DisbursementAccountRow | null>(null);
  useEffect(() => { getDisbursementAccount(facilityId).then(v => setRegistered(v.active)).catch(() => setRegistered(null)); }, [facilityId]);
  // F-MK-041: without a registered account, the account the borrower's notice names (captured on the request) is the instruction.
  const fromNotice = !registered && instructions.bankName && instructions.accountLast4 ? instructions : null;
  const releaseReady = Boolean(release.wireReference.trim() && (registered || fromNotice || (release.bankName.trim() && release.accountLast4.length === 4)));
  const confirmReady = Boolean(confirm.confirmationReference.trim() || confirm.confirmationDocumentId.trim());

  const primaryReason = primary === 'approve' ? (!notes.trim() ? 'Decision notes are required.' : null)
    : primary === 'release' ? (!releaseReady ? (registered || fromNotice ? 'The wire reference is required.' : 'Wire reference, bank and account last four are required.') : null)
    : primary === 'confirm' ? (!confirmReady ? 'A bank confirmation reference or document is required.' : null)
    : null;

  const doPrimary = () => {
    if (primary === 'approve') return run('approve', () => decideControlledDrawRequest(facilityId, request.id, 'APPROVE', notes),
      isReversal ? 'Reversal approved and posted; the original request is marked REVERSED.'
        : isRepayment ? 'Repayment approved; confirm the bank credit to post it.'
        : `Draw approved and ${money(request.amount, currency)} reserved; official IBOR is unchanged until confirmation.`);
    if (primary === 'release') return run('release', () => releaseControlledDrawRequest(facilityId, request.id, {
      valueDate: release.valueDate, wireReference: release.wireReference,
      settlementInstructions: registered || fromNotice ? undefined : { bankName: release.bankName, accountLast4: release.accountLast4, routing: release.routing, beneficiary: release.beneficiary }, notes }),
      `Release recorded: wire ${release.wireReference}, value date ${date(release.valueDate)}. AxiomFlow did not execute a bank payment.`);
    if (primary === 'confirm') return run('confirm', () => confirmFundingRequest(facilityId, request.id, {
      confirmationReference: confirm.confirmationReference || undefined, confirmationDocumentId: confirm.confirmationDocumentId || undefined,
      confirmedValueDate: confirm.confirmedValueDate || undefined, confirmedAmount: confirm.confirmedAmount ? parseAmountShorthand(confirm.confirmedAmount) : undefined, notes }),
      `${money(request.amount, currency)} ${isRepayment ? 'repayment' : 'draw'} confirmed and posted through the lifecycle lane; official IBOR updated.`);
    if (primary === 'retry') return run('retry', () => retryFundingPosting(facilityId, request.id), 'Posting retried and applied.');
    return Promise.resolve(false);
  };

  const primaryLabel = primary === 'approve' ? (isReversal ? 'Approve & post reversal' : isRepayment ? 'Approve repayment' : 'Approve & reserve')
    : primary === 'release' ? 'Record release'
    : primary === 'confirm' ? (isRepayment ? 'Confirm receipt & post' : 'Confirm & post')
    : primary === 'retry' ? 'Retry posting' : null;

  const stateLine = request.attention_reason ? 'Held — clear the block, then continue'
    : primary ? `Your step: ${primaryLabel}`
    : request.status === 'PENDING_APPROVAL' ? 'Waiting for a checker who did not make this request'
    : request.status === 'POSTED' ? 'Posted to official IBOR'
    : plain(request.status);

  // N-LR-014: a step that has its own form (release, confirmation) carries its
  // button inside that form, next to the fields it submits. It used to sit in the
  // bottom bar, with the lineage timeline between the fields and the button.
  const formStep = primary === 'release' || primary === 'confirm';
  const stepActions = formStep && primaryLabel ? <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 14 }}>
    <Input style={{ flex: '1 1 16rem' }} value={notes} placeholder="Notes (optional)" onChange={e => setNotes(e.target.value)} />
    <Button type="button" $variant="primary" disabled={Boolean(primaryReason) || busy !== null} onClick={() => void doPrimary()}>
      {busy ? 'Working…' : <><CheckCircle2 size={14} /> {primaryLabel}</>}</Button>
    {primaryReason ? <small style={{ color: INK.muted }}>{primaryReason}</small> : null}
  </div> : null;

  return <>
    <div className="work-heading">
      <div><h2>{typeWord(request)} request #{request.id} · {money(request.amount, currency)}</h2>
        <p>Value date {date(request.value_date || request.requested_date)} · {request.origin === 'DOCUMENT' ? `from ${model.sourceEvidence.documentName || 'the borrower notice'}` : `entered by ${who(request.requested_by)}`}{request.reversal_of_request_id ? ` · reverses #${request.reversal_of_request_id} (${plain(request.reversal_reason_code)})` : ''}{request.reversed_by_request_id ? ` · reversed by #${request.reversed_by_request_id}` : ''}</p></div>
      <StatusChip tone={statusTone(String(request.status), request.attention_reason)} label={statusWord(request)} size="md" />
    </div>

    <WorkspaceGuide><h2>{primaryLabel ? `Next: ${primaryLabel}` : statusWord(request)}</h2><p>{primaryReason || (primaryLabel ? 'Review the evidence below and complete the required decision fields. The server rechecks the gates when you act.' : 'Review the decision, bank confirmation and posting lineage below.')}</p></WorkspaceGuide>
    {request.attention_reason && <Attention><AlertTriangle size={16} /><div><strong>Needs attention.</strong><p>{request.attention_reason} — since {stamp(request.attention_since)}. The reservation is kept. The next action re-runs the gates and proceeds when they pass; the request can be cancelled at any time.</p></div></Attention>}
    {request.failure_reason && request.status === 'FAILED' && <Attention><AlertTriangle size={16} /><div><strong>Posting failed.</strong><p>{request.failure_reason}. Nothing moved; retry when the cause is fixed, or cancel.</p></div></Attention>}

    <Note style={{ marginBottom: 14 }}>
      <strong>{model.policy.label}</strong> — <span title={`${model.policy.source} = ${model.policy.value}`}>{model.policy.summary}</span>
    </Note>

    <Grid2 style={{ marginTop: 0 }}>
      <Card>
        <CardHead><div><h2>Gates</h2><p>{model.gateHistory.length ? `Ran at ${model.gateHistory.map(g => g.checkpoint.toLowerCase()).join(', ')}; latest result below.` : 'No gate evaluation recorded (this request predates the gate record).'}</p></div>
          {latest?.gates && <StatusChip tone={latest.passed ? 'done' : 'critical'} label={latest.passed ? 'All passed' : `Blocked by ${latest.blockingGate}`} />}</CardHead>
        {latest?.gates?.length ? <GateList gates={latest.gates} /> : null}
      </Card>
      <Card>
        <CardHead><div><h2>Record</h2><p>Who did what, and the authority the request is bound to.</p></div></CardHead>
        <Facts>
          <dt>Maker</dt><dd>{who(request.requested_by)} · {stamp(request.created_at)}</dd>
          <dt>Checker</dt><dd>{request.approved_by ? `${request.approved_by} · ${stamp(request.approved_at)}${request.decision_reason ? ` — “${request.decision_reason}”` : ''}` : '—'}</dd>
          {!isRepayment && !isReversal && <><dt>Authority</dt><dd>Run {plain(model.authorityBundle.borrowingBaseRunId)} · {money(model.authorityBundle.borrowingBaseAmount, currency)} · {plain(model.authorityBundle.borrowingBasePeriod, date(model.authorityBundle.borrowingBaseAsOf))} · {model.authorityBundle.matchesCurrentApprovedAuthority ? 'current' : 'superseded'}</dd>
            <dt>Tape</dt><dd>#{plain(model.authorityBundle.releasedTapeIngestId)} as of {date(model.authorityBundle.releasedTapeAsOf)}</dd></>}
          <dt>Contract</dt><dd>Version {plain(model.authorityBundle.facilityVersionNo)} effective {date(model.authorityBundle.facilityVersionEffectiveFrom)}</dd>
          {model.reservation.id != null && <><dt>Reservation</dt><dd>#{String(model.reservation.id)} · {plain(model.reservation.status)} · {money(model.reservation.amount, currency)}</dd></>}
          {request.released_by && <><dt>Released</dt><dd>{request.released_by} · {stamp(request.released_at)} · wire {model.settlement.wireReference} · {String(instructions.bankName || '')}{instructions.accountLast4 ? ` ****${instructions.accountLast4}` : ''}</dd></>}
          {(model.settlement.confirmationReference || model.settlement.confirmationDocumentId) && <><dt>Bank confirmation</dt><dd>{model.settlement.confirmationReference || `document ${model.settlement.confirmationDocumentId}`} · {date(model.settlement.confirmedValueDate)} · {money(model.settlement.confirmedAmount, currency)}</dd></>}
          {request.confirmed_by && <><dt>Posted</dt><dd>{request.confirmed_by} · {stamp(request.confirmed_at)}</dd></>}
          {request.cancelled_by && <><dt>Cancelled</dt><dd>{request.cancelled_by} · {stamp(request.cancelled_at)} — “{request.decision_reason}”</dd></>}
        </Facts>
      </Card>
    </Grid2>

    {primary === 'release' && <Card style={{ marginTop: 14 }}>
      <CardHead><div><h2>Release the wire</h2><p>What the bank was told. Recorded on the request; AxiomFlow does not execute the payment.</p></div></CardHead>
      <FormGrid>
        <FieldLabel>Value date<DateField value={release.valueDate} today={model.businessDate || undefined} onChange={next => setRelease({ ...release, valueDate: next })} /></FieldLabel>
        <FieldLabel>Wire reference<Input value={release.wireReference} placeholder="FED ref / IMAD" onChange={e => setRelease({ ...release, wireReference: e.target.value })} /></FieldLabel>
        {!registered && !fromNotice && <>
          <FieldLabel>Bank<Input value={release.bankName} placeholder="Beneficiary bank" onChange={e => setRelease({ ...release, bankName: e.target.value })} /></FieldLabel>
          <FieldLabel>Account (last four)<Input maxLength={4} value={release.accountLast4} onChange={e => setRelease({ ...release, accountLast4: e.target.value.replace(/\D/g, '') })} /></FieldLabel>
          <FieldLabel>Routing / SWIFT<Input value={release.routing} onChange={e => setRelease({ ...release, routing: e.target.value })} /></FieldLabel>
          <FieldLabel>Beneficiary<Input value={release.beneficiary} placeholder="Borrower account name" onChange={e => setRelease({ ...release, beneficiary: e.target.value })} /></FieldLabel>
        </>}
      </FormGrid>
      {fromNotice && <p style={{ margin: '8px 0 0', fontSize: 13, color: '#cbd5e1' }}>
        Wires to the account the drawdown notice names: <strong>{String(fromNotice.bankName)}</strong> · ****{String(fromNotice.accountLast4)}{fromNotice.routing ? ` · routing ${String(fromNotice.routing)}` : ''}
        <span style={{ display: 'block', color: '#94a3b8', fontSize: 12 }}>Read from the notice, not typed. No disbursement account is registered for this facility yet: register it on the Borrower disbursement account card (a checker approves) and a later notice naming another account is refused.</span>
      </p>}
      {registered && <p style={{ margin: '8px 0 0', fontSize: 13, color: '#cbd5e1' }}>
        Wires to the registered account: <strong>{registered.beneficiary_name}</strong> · {registered.bank_name} · ****{registered.account_last4} · routing {registered.routing_number}
        <span style={{ display: 'block', color: '#94a3b8', fontSize: 12 }}>Verified by {registered.verification_method === 'CALL_BACK' ? 'call-back' : registered.verification_method.toLowerCase().replace('_', ' ')} with {registered.verified_with}; approved by {registered.decided_by}. A notice naming another account is refused.</span>
      </p>}
      {stepActions}
    </Card>}

    {primary === 'confirm' && <Card style={{ marginTop: 14 }}>
      <CardHead><div><h2>{isRepayment ? 'Bank credit received' : 'Bank confirmed the wire'}</h2><p>The confirmation is the evidence the post carries. A reference or the confirmation document, at least one.</p></div></CardHead>
      <FormGrid>
        <FieldLabel>{isRepayment ? 'Credit advice reference' : 'Confirmation reference'}<Input value={confirm.confirmationReference} onChange={e => setConfirm({ ...confirm, confirmationReference: e.target.value })} /></FieldLabel>
        <FieldLabel>Confirmation document<Input value={confirm.confirmationDocumentId} placeholder="Document Processing job id" onChange={e => setConfirm({ ...confirm, confirmationDocumentId: e.target.value })} /></FieldLabel>
        <FieldLabel>Confirmed value date<DateField value={confirm.confirmedValueDate} today={model.businessDate || undefined} onChange={next => setConfirm({ ...confirm, confirmedValueDate: next })} /></FieldLabel>
        <FieldLabel>Confirmed amount<Input inputMode="decimal" placeholder={money(request.amount, currency)} value={confirm.confirmedAmount} onChange={e => setConfirm({ ...confirm, confirmedAmount: e.target.value })} onBlur={e => setConfirm({ ...confirm, confirmedAmount: replaceShorthandOnBlur(e.target.value) })} />
          {confirm.confirmedAmount.trim() && !isValidAmountShorthand(confirm.confirmedAmount) && <ParsedAmount data-bad>Not a valid amount</ParsedAmount>}</FieldLabel>
      </FormGrid>
      {stepActions}
    </Card>}

    {a.reverse.allowed && <Signoff>
      <summary>Reverse this posting <small>a compensating event; a different checker approves it</small></summary>
      <div>
        <FormGrid style={{ marginTop: 0 }}>
          <FieldLabel>Reason code<Select value={reversal.reasonCode} onChange={e => setReversal({ ...reversal, reasonCode: e.target.value as typeof reversal.reasonCode })}>
            <option value="BOOKING_ERROR">Booking error</option><option value="WIRE_RETURNED">Wire returned</option><option value="DUPLICATE">Duplicate</option><option value="OTHER">Other</option></Select></FieldLabel>
          <FieldLabel>Reversal date<DateField value={reversal.reversalDate} today={model.businessDate || undefined} onChange={next => setReversal({ ...reversal, reversalDate: next })} /></FieldLabel>
          <FieldLabel>Evidence reference<Input value={reversal.evidenceReference} placeholder="Bank return advice, ticket" onChange={e => setReversal({ ...reversal, evidenceReference: e.target.value })} /></FieldLabel>
          <FieldLabel style={{ gridColumn: '1 / -1' }}>Reason<Input value={reversal.reason} onChange={e => setReversal({ ...reversal, reason: e.target.value })} /></FieldLabel>
        </FormGrid>
        <div style={{ marginTop: 12 }}><Button type="button" $variant="danger" disabled={!reversal.reason.trim() || busy !== null}
          onClick={() => void run('reverse', () => proposeFundingReversal(facilityId, request.id, reversal), `Reversal of request #${request.id} proposed; a checker must approve it before the compensating event posts.`)}>
          <RotateCcw size={14} /> Submit reversal for approval</Button></div>
      </div>
    </Signoff>}

    <Signoff open={timelineOpen} onToggle={e => setTimelineOpen(e.currentTarget.open)}>
      <summary>Lineage timeline <small>request → gates → decision → reservation → release → confirmation → trade → posting → position</small></summary>
      <Readable>
        <Timeline>
          <thead><tr><th>When</th><th>Step</th><th>What</th><th>Who</th></tr></thead>
          <tbody>{model.timeline.map((e, i) => <tr key={`${e.state}-${i}`}><td className="when">{stamp(e.at)}</td><td><StatusChip tone={['GATES_FAILED', 'FAILED', 'ATTENTION', 'REJECTED', 'CANCELLED', 'EXPIRED'].includes(e.state) ? 'critical' : ['POSTED', 'POSITION', 'TRADE', 'GATES_PASSED', 'REVERSED'].includes(e.state) ? 'done' : 'progress'} label={plain(e.state)} /></td><td>{e.label}{e.ref ? <small style={{ color: INK.faint }}> · #{e.ref}</small> : null}</td><td>{who(e.actor) === '—' ? 'System' : who(e.actor)}</td></tr>)}</tbody>
        </Timeline>
      </Readable>
    </Signoff>

    <Signoff open={lineageOpen} onToggle={e => setLineageOpen(e.currentTarget.open)}>
      <summary>Official IBOR lineage <small>{lineage.posted ? 'lifecycle event → trade → tax lot → fact_positions → ABF attributes → audit pair' : 'not posted — requests and reservations are not funded positions'}</small></summary>
      <Readable>
        {lineage.posted ? <Facts>
          <dt>Lifecycle event</dt><dd>#{String(lineage.lifecycleEvent?.id)} · {plain(lineage.lifecycleEvent?.event_type)} · {plain(lineage.lifecycleEvent?.status)} · drawn {money(lineage.lifecycleEvent?.before_drawn_amount, currency)} → {money(lineage.lifecycleEvent?.after_drawn_amount, currency)} · evidence {String(lineage.evidenceRef || '—')}</dd>
          <dt>Trade</dt><dd>#{String(lineage.trade?.id)} · {plain(lineage.trade?.ticket_type)} / {plain(lineage.trade?.event_type)} / {plain(lineage.trade?.transaction_type)} · {plain(lineage.trade?.status)} · {date(lineage.trade?.trade_date)} · {String(lineage.sourceRef || '')}</dd>
          <dt>Tax lots</dt><dd>{(lineage.taxLots || []).map(l => `#${l.id} ${money(l.current_quantity, currency)} (${plain(l.status)})`).join(' · ') || '—'}</dd>
          <dt>Position</dt><dd>#{String(lineage.officialPosition?.id)} · {money(lineage.officialPosition?.quantity, currency)} · as of {date(lineage.officialPosition?.as_of_date)}</dd>
          <dt>ABF attributes</dt><dd>{money(lineage.abfAttributes?.drawn_amount, currency)} drawn · {money(lineage.abfAttributes?.undrawn_amount, currency)} undrawn · {money(lineage.abfAttributes?.excess_availability, currency)} availability</dd>
          <dt>Audit</dt><dd>{plural((lineage.postingAudit || []).length, 'posting audit row')}, {plural(new Set((lineage.postingAudit || []).map(r => String(r.correlation_id))).size, 'correlation id')}</dd>
          {lineage.reversalEvent?.id ? <><dt>Reversed by</dt><dd>lifecycle event #{String(lineage.reversalEvent.id)} (request #{String(lineage.reversalEvent.draw_request_id)})</dd></> : null}
        </Facts> : <p style={{ color: INK.muted }}>Nothing has posted for this request.</p>}
      </Readable>
    </Signoff>

    <Bar>
      <div className="state">{stateLine}{formStep ? <> — complete the form above</> : primaryReason && primary ? <> — <strong>{primaryReason}</strong></> : null}</div>
      <div className="actions">
        {primary === 'approve' && <input value={notes} placeholder="Decision notes (required)" onChange={e => setNotes(e.target.value)} />}
        {(a.reject.allowed || a.cancel.allowed) && <input value={reason} placeholder={a.reject.allowed ? 'Reason to reject or cancel (required)' : 'Reason to cancel (required)'} onChange={e => setReason(e.target.value)} />}
        {a.reject.allowed && <Button type="button" $variant="danger" disabled={!reason.trim() || busy !== null} title={!reason.trim() ? 'A reason is required' : undefined}
          onClick={() => void run('reject', () => decideControlledDrawRequest(facilityId, request.id, 'REJECT', reason), 'Request rejected; nothing was reserved or posted.')}><XCircle size={14} /> Reject</Button>}
        {a.cancel.allowed && <Button type="button" $variant="ghost" disabled={!reason.trim() || busy !== null} title={!reason.trim() ? 'A reason is required' : undefined}
          onClick={() => void run('cancel', () => cancelFundingRequest(facilityId, request.id, reason), 'Request cancelled and any reservation released.')}>Cancel request</Button>}
        {primary && primaryLabel && !formStep && <Button type="button" $variant="primary" disabled={Boolean(primaryReason) || busy !== null} onClick={() => void doPrimary()}>
          {busy ? 'Working…' : <><CheckCircle2 size={14} /> {primaryLabel}</>}</Button>}
        {!primary && !a.reject.allowed && !a.cancel.allowed && <SmallButton type="button" disabled>No action for you on this request</SmallButton>}
      </div>
    </Bar>
  </>;
};

export default FundingWorkspace;
